// Settings → Shared costs (rev. 1.18, Chris 2026-10-15): business costs that belong to no client and
// no person, as a yearly budget and monthly actuals by category, spread over the hours people work,
// FTEs and 1099s alike. The plan rate (budget ÷ the team's expected hours) sets the project editor's
// fully burdened view; each month's actual rate (actuals ÷ hours reported) feeds Performance.

import { useEffect, useState } from "react";
import { Body1, Button, Caption1, Card, Checkbox, Input, Select, Subtitle2, Switch } from "@fluentui/react-components";
import { type SharedCostsDoc } from "../../../core";
import { errorText, send, useApi } from "../api";
import { money, parseAmount, perHour } from "../format";
import { ErrorBar, Kpi, Loading, useMe, Warnings } from "../ui";

interface SharedCostsResponse {
  year: number;
  doc: SharedCostsDoc;
  etag: string | null;
  plan: { rate: number; budget: number; hours: number } | null;
  months: { month: string; rate: number; actual: number | null; hours: number; fromPlan: boolean; fromBooks?: boolean }[];
  /** Past months' actuals read from QuickBooks (FR10.7), which replace what was entered for them. */
  booksActuals?: { month: string; categoryId: string; amount: number }[];
  people: { id: string; name: string; carries: boolean; type: string | null }[];
  /** Approved expense-report spend on 3S001–3S003 this year, by category (ER7). */
  drawdown?: Record<string, number>;
}

const MONTHS = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
const h = (n: number) => `${Math.round(n).toLocaleString("en-US")} h`;

export function SharedCosts() {
  const me = useMe();
  const editable = me.can("people.edit");
  const [year, setYear] = useState(new Date().getFullYear());
  const data = useApi<SharedCostsResponse>(`/shared-costs?year=${year}`);
  const [doc, setDoc] = useState<SharedCostsDoc | null>(null);
  const [text, setText] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setDoc(data.data ? structuredClone(data.data.doc) : null);
    setText({});
  }, [data.data]);
  const d = data.data;
  if (!d || !doc) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  const dirty = JSON.stringify(doc) !== JSON.stringify(d.doc);
  const cats = doc.categories;

  // Typed amounts are kept as text until they parse, so "1,2" doesn't jump to 12.
  const field = (key: string, value: number | undefined) => text[key] ?? (value ? String(value) : "");
  const setAmount = (key: string, raw: string, apply: (n: number) => void) => {
    setText({ ...text, [key]: raw });
    setSaved(false);
    const n = raw.trim() === "" ? 0 : parseAmount(raw);
    if (Number.isFinite(n)) apply(n);
  };
  const budgetOf = (categoryId: string) => doc.budgets.find((b) => b.year === year && b.categoryId === categoryId)?.annual ?? 0;
  const setBudget = (categoryId: string, annual: number) =>
    setDoc({ ...doc, budgets: [...doc.budgets.filter((b) => !(b.year === year && b.categoryId === categoryId)), ...(annual ? [{ year, categoryId, annual }] : [])] });
  const actualOf = (month: string, categoryId: string) => doc.actuals.find((a) => a.month === month && a.categoryId === categoryId)?.amount;
  const setActual = (month: string, categoryId: string, amount: number, clear: boolean) =>
    setDoc({ ...doc, actuals: [...doc.actuals.filter((a) => !(a.month === month && a.categoryId === categoryId)), ...(clear ? [] : [{ month, categoryId, amount }])] });
  const budgetTotal = cats.reduce((t, c) => t + budgetOf(c.id), 0);

  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/shared-costs", { etag: d!.etag, doc });
      setSaved(true);
      data.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <>
      <Card className="panel wide">
        <div className="panel-head">
          <Subtitle2>Shared costs, {year}</Subtitle2>
          <div className="toolbar">
            <Select size="small" value={String(year)} onChange={(_, x) => setYear(Number(x.value))} aria-label="Year">
              {[year - 1, year, year + 1].map((y) => (
                <option key={y}>{y}</option>
              ))}
            </Select>
            {editable && (
              <Button appearance="primary" disabled={!dirty || busy} onClick={() => void save()}>
                {busy ? "Saving…" : dirty ? "Save" : "Saved"}
              </Button>
            )}
          </div>
        </div>
        <Caption1 className="block">
          Costs that support the work but belong to no client and no person: shared services, software, insurance, travel not billed to a client, admin.
          They are spread over the hours people work, FTEs and 1099s alike, not over pay. Travel billed to a client stays a project expense, and costs
          tied to one person (healthcare, a laptop) stay on their People card. Shared costs never appear in anyone's own pay.
        </Caption1>
        <ErrorBar message={error} />
        {saved && !dirty && <Warnings intent="info" items={["Saved. Projects and Performance use the new rates."]} />}
        <div className="kpis">
          <Kpi label="Budget" value={money(budgetTotal)} sub={`${year}, all categories`} />
          <Kpi
            label="Drawn down by expense reports"
            value={money(Object.values(d.drawdown ?? {}).reduce((t, v) => t + v, 0))}
            sub={`approved lines on 3S001–3S003; ${money(budgetTotal - Object.values(d.drawdown ?? {}).reduce((t, v) => t + v, 0))} left`}
          />
          <Kpi label="Team's expected hours" value={d.plan ? h(d.plan.hours) : "—"} sub="productive hours of everyone who carries shared costs" />
          <Kpi label="Plan rate" value={d.plan ? perHour(d.plan.rate) : "—"} sub={d.plan ? "on every planned hour; the fully burdened view" : "enter a budget to set it"} />
        </div>
        <table className="edit-table">
          <thead>
            <tr>
              <th>Category</th>
              <th className="num">Budget, {year}</th>
              <th className="num" title="Approved expense reports coded to 3S001, 3S002 or 3S003, in this category">Expense reports</th>
              <th className="num">Left</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c, i) => (
              <tr key={c.id}>
                <td>
                  <Input size="small" value={c.name} disabled={!editable} onChange={(_, x) => setDoc({ ...doc, categories: cats.map((y, j) => (j === i ? { ...y, name: x.value } : y)) })} aria-label="Category name" style={{ width: "22em" }} />
                </td>
                <td className="num">
                  <Input size="small" contentBefore="$" disabled={!editable} value={field(`b:${c.id}`, budgetOf(c.id))} onChange={(_, x) => setAmount(`b:${c.id}`, x.value, (n) => setBudget(c.id, n))} aria-label={`Budget for ${c.name}`} />
                </td>
                <td className="num">{money(d.drawdown?.[c.id] ?? 0)}</td>
                <td className={`num ${budgetOf(c.id) - (d.drawdown?.[c.id] ?? 0) < 0 ? "delta-bad" : ""}`}>{budgetOf(c.id) ? money(budgetOf(c.id) - (d.drawdown?.[c.id] ?? 0)) : "—"}</td>
                <td>
                  <Switch checked={c.active} disabled={!editable} onChange={(_, x) => setDoc({ ...doc, categories: cats.map((y, j) => (j === i ? { ...y, active: x.checked } : y)) })} aria-label="Active" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {editable && (
          <div className="toolbar">
            <Button size="small" onClick={() => setDoc({ ...doc, categories: [...cats, { id: `c-${Date.now().toString(36)}`, name: "New category", active: true }] })}>
              Add a category
            </Button>
          </div>
        )}
      </Card>

      <Card className="panel wide">
        <Subtitle2>Actuals by month</Subtitle2>
        <Caption1 className="block">
          What was actually spent. Each month's actual rate is its total ÷ the hours reported that month by everyone who carries shared costs; Performance
          uses it for reported hours. A month left blank uses the plan rate. Once a QuickBooks spending category feeds a shared-cost category (QuickBooks → Categories), each past month
          QuickBooks has read takes its company spending from there instead; budgets still drive the forecast.
        </Caption1>
        <div className="table-scroll">
          <table className="edit-table compact">
            <thead>
              <tr>
                <th>Month</th>
                {cats.filter((c) => c.active).map((c) => (
                  <th key={c.id} className="num">
                    {c.name}
                  </th>
                ))}
                <th className="num">Total</th>
                <th className="num">Hours reported</th>
                <th className="num">Rate</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((mm) => {
                const month = `${year}-${mm}`;
                const row = d.months.find((m) => m.month === month);
                const fromBooks = !!row?.fromBooks;
                const booksOf = (categoryId: string) => (d.booksActuals ?? []).filter((a) => a.month === month && a.categoryId === categoryId).reduce((t, a) => t + a.amount, 0);
                const total = fromBooks ? cats.reduce((t, c) => t + booksOf(c.id), 0) : cats.reduce((t, c) => t + (actualOf(month, c.id) ?? 0), 0);
                const entered = fromBooks || cats.some((c) => actualOf(month, c.id) !== undefined);
                return (
                  <tr key={month}>
                    <td>
                      {month}
                      {fromBooks && <Caption1 className="block muted">From QuickBooks</Caption1>}
                    </td>
                    {cats.filter((c) => c.active).map((c) =>
                      fromBooks ? (
                        <td key={c.id} className="num">
                          {money(booksOf(c.id))}
                        </td>
                      ) : (
                      <td key={c.id} className="num">
                        <Input
                          size="small"
                          disabled={!editable}
                          value={field(`a:${month}:${c.id}`, actualOf(month, c.id))}
                          onChange={(_, x) => setAmount(`a:${month}:${c.id}`, x.value, (n) => setActual(month, c.id, n, x.value.trim() === ""))}
                          aria-label={`${c.name}, ${month}`}
                          style={{ width: "8em" }}
                        />
                      </td>
                      ),
                    )}
                    <td className="num">{entered ? money(total) : ""}</td>
                    <td className="num">{row && row.hours ? h(row.hours) : ""}</td>
                    <td className="num">
                      {row && row.rate ? perHour(row.rate) : ""}
                      {row && row.fromPlan && row.rate > 0 && <Caption1 className="block muted">plan</Caption1>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {dirty && <Caption1 className="muted">Rates update once saved.</Caption1>}
      </Card>

      <Card className="panel wide">
        <Subtitle2>Who carries shared costs</Subtitle2>
        <Caption1 className="block">
          Everyone does by default: the shared services support the work whoever does it, and leaving 1099 providers out would make them look cheaper
          than they are. Untick someone only for an exception, such as a pure pass-through subcontractor.
        </Caption1>
        <div className="toolbar">
          {d.people.map((p) => (
            <Checkbox
              key={p.id}
              label={p.name}
              disabled={!editable}
              checked={!doc.excluded.includes(p.id)}
              onChange={(_, x) => setDoc({ ...doc, excluded: x.checked ? doc.excluded.filter((id) => id !== p.id) : [...doc.excluded, p.id] })}
            />
          ))}
        </div>
        {!d.people.length && <Body1 className="muted">No one yet.</Body1>}
      </Card>
    </>
  );
}
